"use client";

import Link from "next/link";
import { signOut, useSession } from "next-auth/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type LandingUser,
  resolveLandingAuthState,
} from "@/components/landing/landing-auth-state";

export default function GraphifyLandingHeaderAccount({
  locale,
  initialUser,
  callbackUrl,
}: {
  locale: string;
  /** From server `auth()` so the header is correct before SessionProvider hydrates. */
  initialUser?: LandingUser | null;
  callbackUrl?: string;
}) {
  return (
    <GraphifyLandingHeaderAccountWithSession
      locale={locale}
      initialUser={initialUser}
      callbackUrl={callbackUrl}
    />
  );
}

function GraphifyLandingHeaderAccountWithSession({
  locale,
  initialUser,
  callbackUrl,
}: {
  locale: string;
  initialUser?: LandingUser | null;
  callbackUrl?: string;
}) {
  const { data: session, status } = useSession();
  const { user, signedIn } = resolveLandingAuthState(status, session, initialUser);

  const avatarSrc = user?.avatar_url || user?.image || undefined;
  const displayName =
    user?.nickname || user?.name || user?.email?.split("@")[0] || "User";
  const email = user?.email ?? "";

  if (status === "loading" && initialUser === undefined) {
    return (
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="h-9 w-24 animate-pulse rounded-full bg-white/10" />
        <div className="h-9 w-28 animate-pulse rounded-full bg-white/10" />
      </div>
    );
  }

  if (signedIn && user) {
    return (
      <div className="flex items-center gap-2 sm:gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="graphify-account-menu-trigger"
              className="rounded-full outline-none ring-offset-2 ring-offset-[var(--graphify-canvas)] transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-sky-400"
              aria-label="Account menu"
            >
              <Avatar className="size-9 border border-white/25 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
                <AvatarImage src={avatarSrc} alt={displayName} />
                <AvatarFallback className="bg-gradient-to-br from-sky-500 to-slate-700 text-sm font-semibold text-white">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="z-50 w-72 border border-white/10 bg-[#0c1829]/95 p-2 text-slate-100 shadow-2xl backdrop-blur-xl"
          >
            <div className="px-2 py-2">
              <p className="truncate text-sm font-medium text-white">{displayName}</p>
              {email ? (
                <p className="mt-0.5 truncate text-xs text-slate-400">{email}</p>
              ) : null}
            </div>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              asChild
              className="cursor-pointer rounded-lg text-slate-200 focus:bg-white/10 focus:text-white"
            >
              <Link href={`/${locale}/graphs`} prefetch={false}>
                My graphs
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              asChild
              className="cursor-pointer rounded-lg text-slate-200 focus:bg-white/10 focus:text-white"
            >
              <Link href={`/${locale}/my-credits`} prefetch={false}>
                My credits
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              className="cursor-pointer rounded-lg text-slate-200 focus:bg-white/10 focus:text-white"
              onClick={() => signOut({ callbackUrl: `/${locale}` })}
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return <SignedOutHeaderActions locale={locale} callbackUrl={callbackUrl} />;
}

function SignedOutHeaderActions({
  locale,
  callbackUrl,
}: {
  locale: string;
  callbackUrl?: string;
}) {
  const afterSignInUrl = callbackUrl ?? `/${locale}`;

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <Button
        asChild
        variant="graphifySecondary"
        size="sm"
        className="rounded-full"
      >
        <Link
          href={`/${locale}/auth/signin?callbackUrl=${encodeURIComponent(afterSignInUrl)}`}
        >
          Sign in
        </Link>
      </Button>
    </div>
  );
}
